import React, { useState, useEffect } from 'react';
import { BookOpen, ScrollText, Scale, Waypoints } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import SignificadoSection from '@/components/SignificadoSection';
import TeachingsSection from '@/components/TeachingsSection';
import MaterialSection from '@/components/MaterialSection';
import { tx } from '@/translations/translations';

const OPTIONS = [
  { id: 'significado', icon: BookOpen, es: 'Significado', en: 'Meaning', he: 'משמעות', desc: { es: 'Por qué usamos tzitzit', en: 'Why we wear tzitzit', he: 'למה לובשים ציצית' } },
  { id: 'halajot', icon: Scale, es: 'Halajot', en: 'Halakhot', he: 'הלכות', desc: { es: 'Guía práctica de leyes', en: 'Practical law guide', he: 'מדריך הלכתי מעשי' } },
  { id: 'material', icon: ScrollText, es: 'Material', en: 'Material', he: 'חומרים', desc: { es: 'Debate talmúdico y amarres', en: 'Talmudic debate & knotting', he: 'דיון תלמודי וקשירות' } },
  { id: 'meritos', icon: Waypoints, es: 'Lo que Dicen Nuestros Sabios', en: 'What Our Sages Say', he: 'דברי חכמינו', desc: { es: 'Méritos del tzitzit', en: 'Merits of tzitzit', he: 'מעלות הציצית' } },
];

const MasDelTzitzitHub = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(null);

  const toggle = (id) => setActiveSection(activeSection === id ? null : id);

  // Listen for header nav events
  useEffect(() => {
    const handler = (e) => setActiveSection(e.detail);
    window.addEventListener('openHubSection', handler);
    return () => window.removeEventListener('openHubSection', handler);
  }, []);

  return (
    <div id="mas-del-tzitzit">
      {/* Hub — always visible */}
      <section className="py-10 md:py-16 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-400/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span className="text-cyan-300 text-[11px] font-bold uppercase tracking-[0.2em]">
                {tx(language, 'Más del Tzitzit', 'More about Tzitzit', 'עוד על הציצית')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {tx(language, 'Aprende lo Básico', 'Learn the Basics', 'למדו את הבסיס')}
            </h2>
            <p className="text-sm text-slate-400">
              {tx(language, 'Todo lo que necesitas saber de tu tzitzit en 4 minutos', 'Everything you need to know about your tzitzit in 4 minutes', 'כל מה שצריך לדעת על הציצית ב-4 דקות')}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = activeSection === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  className={`text-left rounded-xl p-3 sm:p-5 border transition-all duration-300 ${
                    isActive
                      ? 'bg-cyan-500/10 border-cyan-400/40 shadow-lg shadow-cyan-500/10'
                      : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.07] hover:border-white/[0.15]'
                  }`}
                  data-testid={`hub-${opt.id}`}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 mb-2 sm:mb-3 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                  <p className={`font-semibold text-xs sm:text-sm leading-tight ${isActive ? 'text-white' : 'text-slate-300'}`}>
                    {tx(language, opt.es, opt.en, opt.he)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">
                    {tx(language, opt.desc.es, opt.desc.en, opt.desc.he)}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conditionally render selected section */}
      {activeSection === 'significado' && <SignificadoSection />}
      {activeSection === 'halajot' && <TeachingsSection />}
      {activeSection === 'material' && <MaterialSection />}
      {activeSection === 'meritos' && <MeritosOnly />}
    </div>
  );
};

/* Extract only the meritos part from TeachingsSection */
const MeritosOnly = () => {
  const { language } = useLanguage();
  const [openCard, setOpenCard] = useState(null);
  const toggleCard = (idx) => setOpenCard(openCard === idx ? null : idx);

  const MERITS = [
    { rabbi: 'Baal HaTurim', quote: '8 hilos recuerdan los 8 órganos que el hombre debe cuidar', ref: 'Bamidbar 15:38' },
    { rabbi: 'Baal Shem Tov', quote: 'El mérito del tzitzit trae bendición y sustento a quien lo porta', ref: 'Kéter Shem Tov' },
    { rabbi: 'Ben Ish Jai', quote: 'La mitzvá de Tzitzit trae larga vida', ref: 'Einei Kol Jai' },
    { rabbi: 'El Arizal', quote: 'Mirar los tzitziot anula el enojo', ref: 'Pri Etz Jaim' },
    { rabbi: 'El Jida', quote: 'Gracias al mérito del tzitzit, los pecados son perdonados', ref: 'Pnei David Shelaj 3' },
    { rabbi: 'Gaón de Vilna', quote: 'Otorga claridad para entender los secretos profundos de la Torá', ref: 'Aderet Eliyahu 15:39' },
    { rabbi: 'Jafetz Jaim', quote: 'El tzitzit es un escudo contra el lashon hara', ref: 'Shm. HaLashon 4' },
    { rabbi: 'Jafetz Jaim', quote: 'Mirar los hilos en la oración elimina pensamientos extraños y concentra el corazón', ref: 'Mishná Berurá 24:1' },
    { rabbi: 'Jafetz Jaim', quote: 'Quien cumple con alegría el tzitzit, su hogar y descendencia se iluminan con Torá', ref: 'Likutey Halajot' },
    { rabbi: 'Jafetz Jaim', quote: 'Si llega a pecar, el tzitzit lo despertará al arrepentimiento', ref: 'Mishná Berurá 24:6' },
    { rabbi: 'Kaf Hajaim', quote: 'El tzitzit protege contra el Ayin hara', ref: 'Oraj J. 24:4' },
    { rabbi: 'Midrash Tanjuma', quote: 'El tzitzit es cuerda para quien cae al agua; mientras te aferres, estarás vivo', ref: 'Shlaj 15' },
    { rabbi: 'R. Abraham Danzig', quote: 'Mirar los hilos por la mañana ilumina el alma todo el día', ref: 'Jayé Adam 11:1' },
    { rabbi: 'R. Chaim Shmuelevitz', quote: 'El tzitzit revela nuestro potencial interno y recuerda nuestra misión', ref: 'Sijot Hitjaskut Shelaj 5757' },
    { rabbi: 'R. Elazar ben Yaakov', quote: 'Quien tiene tefilín, mezuzá y tzitzit, no pecará', ref: 'Menajot 43b' },
    { rabbi: 'R. Israel Salanter', quote: 'Si al ver los hilos no siente el deseo de superarse, no ha comprendido su peso', ref: 'Or Yisrael Netiv Or 1' },
    { rabbi: 'R. Itzjak Hutner', quote: 'Su mérito del tzitzit otorga fuerza para levantarse tras cada caída', ref: 'Pajad Itzjak 12' },
    { rabbi: 'R. Itzjak Palaggi', quote: 'El tzitzit otorga mérito a gran riqueza, abundancia y bendiciones sin medida', ref: 'Yafe Lalev Oraj J. 24' },
    { rabbi: 'R. Jaim de Volozhin', quote: 'El tzitzit purifica el corazón y lo prepara para la sabiduría de la Torá', ref: 'Ruaj Jaim' },
    { rabbi: 'R. Jonathan Sacks', quote: 'El tzitzit da identidad clara en un mundo de identidades confusas', ref: 'Cartas a las Próx. Gen.' },
    { rabbi: 'R. Moshe Feinstein', quote: 'Llevar tzitzit es reconocer que el hombre nunca está libre de su responsabilidad', ref: 'Igrot Moshé OJ I' },
    { rabbi: 'R. Noaj Weinberg', quote: 'El tzitzit es la identificación espiritual del judío; nos recuerda quiénes somos', ref: '48 Caminos, p.12' },
    { rabbi: 'R. Samson R. Hirsch', quote: 'El tzitzit es atuendo de modestia; refina el carácter y los modales', ref: 'Horeb Cap. 22' },
    { rabbi: 'R. Shimon bar Yojai', quote: 'Quien la cuida con dedicación amerita recibir la Shejiná', ref: 'Menajot 43B' },
    { rabbi: 'R. Yosef Caro', quote: 'La gran mitzvá de tzitzit nos guía y lleva hacia el Olam Haba', ref: 'Beit Yosef 24' },
    { rabbi: 'Rambam', quote: 'El tzitzit protege del pecado, al verlo se recuerda al Creador', ref: 'Mishné Torá, Tzitzit 3:12' },
    { rabbi: 'Ramjal', quote: 'Mirar el tzitzit despierta en el Irat Shamaim y refina sus Midot', ref: 'Mesilat Yesh. 4' },
    { rabbi: 'Sefer Hajinuj', quote: 'Tras las acciones físicas, son atraídos los corazones', ref: 'Mitzvá 16, 386' },
    { rabbi: 'Sfat Emet', quote: 'La mitzvá de Tzitzit trae sabiduría y memoria', ref: 'P. Shelaj 5635' },
    { rabbi: 'Sforno', quote: 'El tzitzit ayuda a controlar los deseos y la lujuria de los ojos', ref: 'Bamidbar 15:39' },
    { rabbi: 'Shulján Aruj HaRav', quote: 'Quien es cuidadoso con los tzitzit, sus hijos serán sabios y temerosos de Dios', ref: 'Hiljot Tzitzit 8' },
    { rabbi: 'Talmud Babli', quote: 'El tejelet se asemeja al mar, el mar al cielo y el cielo al Trono de Gloria', ref: 'Menajot 43b' },
    { rabbi: 'Talmud Babli', quote: 'La mitzvá de tzitzit equivale a todas las mitzvot juntas', ref: 'Menajot 43B' },
  ];

  // Group quotes by rabbi — preserve insertion order (already alphabetical)
  const RABBIS = MERITS.reduce((acc, item) => {
    const last = acc[acc.length - 1];
    if (last && last.rabbi === item.rabbi) {
      last.quotes.push({ quote: item.quote, ref: item.ref });
    } else {
      acc.push({ rabbi: item.rabbi, quotes: [{ quote: item.quote, ref: item.ref }] });
    }
    return acc;
  }, []);

  return (
    <section id="meritos" className="py-10 md:py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/[0.04] rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-1.5 mb-5">
            <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-cyan-300 text-xs font-bold uppercase tracking-widest">
              {tx(language, 'Méritos del Tzitzit', 'Merits of Tzitzit', 'מעלות הציצית')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {tx(language, 'Lo que Dicen Nuestros Sabios', 'What Our Sages Say', 'דברי חכמינו')}
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto">
            {tx(language, 'Toca cada tarjeta para descubrir la enseñanza', 'Tap each card to discover the teaching', 'לחצו על כל כרטיס לגילוי הלימוד')}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {RABBIS.map((item, idx) => {
            const isOpen = openCard === idx;
            return (
              <button
                key={item.rabbi}
                onClick={() => toggleCard(idx)}
                className={`text-left rounded-2xl transition-all duration-300 overflow-hidden group ${
                  isOpen
                    ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/10 border border-cyan-400/40 shadow-lg shadow-cyan-500/10'
                    : 'bg-white/[0.04] border border-white/[0.08] hover:border-cyan-400/30 hover:bg-white/[0.07]'
                }`}
                data-testid={`merit-card-${idx}`}
              >
                <div className={`p-4 sm:p-5 ${isOpen ? 'pb-2' : ''}`}>
                  <p className={`font-semibold text-sm leading-snug ${isOpen ? 'text-white' : 'text-slate-300 group-hover:text-white'} transition-colors`}>
                    {item.rabbi}
                  </p>
                </div>
                {isOpen && (
                  <div className="px-4 sm:px-5 pb-5 space-y-3">
                    {item.quotes.map((q, qIdx) => (
                      <div key={q.ref + qIdx} className={qIdx > 0 ? 'pt-3 border-t border-cyan-400/15' : ''}>
                        <p className="text-cyan-100/80 text-sm leading-relaxed mb-2 italic">"{q.quote}"</p>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-px bg-cyan-400/40" />
                          <p className="text-cyan-400/50 text-[11px] font-medium tracking-wide">{q.ref}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MasDelTzitzitHub;
